import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';

import CustomerDashboard from './pages/CustomerDashboard';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CheckoutPage from './pages/CheckoutPage';

import OrderSuccessPage from './pages/OrderSuccessPage';
import AddressFormView from './pages/AddressFormView';
import PaymentMethodsView from './pages/PaymentMethodsView';
import RestaurantDetailPage from './pages/RestaurantDetailPage';
import RestaurantTypePage from './pages/RestaurantTypePage';
import CategoryDetailPage from './pages/CategoryDetailPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationDetailPage from './pages/NotificationDetailPage';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { AddressProvider } from './contexts/AddressContext';
import { PaymentMethodsProvider } from './contexts/PaymentMethodsContext';
import { FirebaseProvider, useFirebase } from './contexts/FirebaseContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import RestaurantPortal from './portals/RestaurantPortal';
import DriverPortal from './portals/DriverPortal';
import SplashScreen from './components/SplashScreen';
import { routeForRole } from './utils/roleRouter';

function NotificationSetup() {
  const { currentUser } = useAuth();
  const { requestNotificationPermissionAndSaveToken } = useFirebase();

  useEffect(() => {
    if (currentUser) {
      requestNotificationPermissionAndSaveToken(currentUser);
    }
  }, [currentUser, requestNotificationPermissionAndSaveToken]);

  return null;
}

// Signed-in restaurant/driver users landing on the customer root get routed
// to their portal — mirrors the iOS single-app role routing.
function RoleAwareHome() {
  const { currentUser, loading } = useAuth();
  const { db } = useFirebase();
  const [destination, setDestination] = React.useState(null);
  const [checked, setChecked] = React.useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading) return;
    if (!currentUser) { setChecked(true); return; }
    routeForRole(db, currentUser.uid).then((path) => {
      if (!cancelled) { setDestination(path); setChecked(true); }
    });
    return () => { cancelled = true; };
  }, [currentUser, loading, db]);

  if (!checked) {
    // Brand-red loader so any gap after the splash stays on-brand, never white.
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #a60d0d 0%, #d91f1f 55%, #ff3b30 100%)' }}
      >
        <div
          className="w-10 h-10 rounded-full border-4 border-white/30 border-t-white animate-spin"
        />
      </div>
    );
  }
  if (destination && destination !== '/') return <Navigate to={destination} replace />;
  return <CustomerDashboard />;
}

// Holds the splash until auth resolves (or the minimum display time passes),
// so the red splash never hands off to a white loading screen.
function AppSplash({ onFinished }) {
  const { loading } = useAuth();
  return <SplashScreen ready={!loading} onFinished={onFinished} />;
}

function App() {
  const [showSplash, setShowSplash] = React.useState(true);

  return (
    <FirebaseProvider>
      <Router>
        <AuthProvider>
          <NotificationSetup />
          <CartProvider>
            <FavoritesProvider>
              <NotificationsProvider>
                <AddressProvider>
                  <PaymentMethodsProvider>
                    <div className="min-h-screen overflow-y-auto">
                      <Routes>
                        {/* Customer Dashboard - Contains all main pages with integrated NavBar */}
                        <Route path="/dashboard/*" element={<CustomerDashboard />} />
                        
                        {/* Authentication pages */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                        {/* Role portals — same sign-in, routed by users/{uid}.role */}
                        <Route path="/business/*" element={<RestaurantPortal />} />
                        <Route path="/driver/*" element={<DriverPortal />} />
                        
                        {/* Standalone pages */}
                        <Route path="/checkout" element={<CheckoutPage />} />

                        <Route path="/order-success" element={<OrderSuccessPage />} />
                        <Route path="/add-address" element={<AddressFormView />} />
                        <Route path="/edit-address/:addressId" element={<AddressFormView />} />
                        <Route path="/payment-methods" element={<PaymentMethodsView />} />
                        <Route path="/restaurant/:restaurantId" element={<RestaurantDetailPage />} />
                        <Route path="/restaurant-type/:restaurantType" element={<RestaurantTypePage />} />
                        <Route path="/category/:category" element={<CategoryDetailPage />} />
                        <Route path="/orders/:orderId" element={<OrderDetailPage />} />
                        <Route path="/orders/:orderId/track" element={<OrderTrackingPage />} />
                        <Route path="/notifications/:notificationId" element={<NotificationDetailPage />} />
                        
                        {/* Redirect root to dashboard (or role portal) */}
                        <Route path="/" element={<RoleAwareHome />} />
                        
                        {/* Legacy routes - redirect to dashboard equivalents */}
                        <Route path="/menu" element={<Navigate to="/dashboard/menu" replace />} />
                        <Route path="/cart" element={<Navigate to="/dashboard/cart" replace />} />
                        <Route path="/orders" element={<Navigate to="/dashboard/orders" replace />} />
                        <Route path="/favorites" element={<Navigate to="/dashboard/favorites" replace />} />
                        <Route path="/notifications" element={<Navigate to="/dashboard/notifications" replace />} />
                        <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
                      </Routes>
                    </div>
                    {showSplash && <AppSplash onFinished={() => setShowSplash(false)} />}
                  </PaymentMethodsProvider>
                </AddressProvider>
              </NotificationsProvider>
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </Router>
    </FirebaseProvider>
  );
}

export default App;
